import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { SqlService, ZohoSetupFeature, ZohoSetupProject } from '../../../services/sql-service/sql-service.service';
import { ZohoBooksService, ZohoProject, ZohoTask } from '../../../services/zoho-books-service/zoho-books-service.service';
import { AuthenticationFeatureService } from '../../../features/authentication-feature/authentication-feature.service';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
  selector: 'app-zoho-setup',
  standalone: true,
  imports: [MatTableModule, MatProgressSpinnerModule, MatFormFieldModule, MatInputModule, MatAutocompleteModule],
  templateUrl: './zoho-setup.component.html',
  styleUrl: './zoho-setup.component.css',
})
export class ZohoSetupComponent implements OnInit {
  private _sqlService = inject(SqlService);
  private _zohoBooksService = inject(ZohoBooksService);
  private _authFeature = inject(AuthenticationFeatureService);

  projects = signal<ZohoSetupProject[]>([]);
  selectedProject = signal<ZohoSetupProject | null>(null);
  features = signal<ZohoSetupFeature[]>([]);

  zohoProjects = signal<ZohoProject[]>([]);
  zohoTasks = signal<ZohoTask[]>([]);

  zohoProjectSearch = signal('');
  zohoTaskSearch = signal('');

  filteredZohoProjects = computed(() => {
    const search = this.zohoProjectSearch().toLowerCase();
    if (!search) return this.zohoProjects();
    return this.zohoProjects().filter((p) => p.project_name.toLowerCase().includes(search));
  });

  filteredZohoTasks = computed(() => {
    const search = this.zohoTaskSearch().toLowerCase();
    if (!search) return this.zohoTasks();
    return this.zohoTasks().filter((t) => t.task_name.toLowerCase().includes(search));
  });

  isLoadingProjects = signal(false);
  isLoadingFeatures = signal(false);
  isLoadingZohoProjects = signal(false);
  isLoadingZohoTasks = signal(false);

  readonly projectColumns = ['projectNo', 'name', 'zohoProjectId'];
  readonly featureColumns = ['phaseNo', 'name', 'zohoTaskId'];

  private get userEmail(): string {
    return this._authFeature.getInstance().getActiveAccount()?.username ?? '';
  }

  ngOnInit() {
    this.isLoadingProjects.set(true);
    this._sqlService.fetchZohoSetupProjects().subscribe({
      next: (data) => { this.projects.set(data); this.isLoadingProjects.set(false); },
      error: () => this.isLoadingProjects.set(false),
    });

    this.isLoadingZohoProjects.set(true);
    this._zohoBooksService.fetchZohoProjects(this.userEmail).subscribe({
      next: (data) => { this.zohoProjects.set(data.projects ?? []); this.isLoadingZohoProjects.set(false); },
      error: () => this.isLoadingZohoProjects.set(false),
    });
  }

  selectProject(project: ZohoSetupProject) {
    this.selectedProject.set(project);
    this.features.set([]);
    this.zohoTasks.set([]);

    this.isLoadingFeatures.set(true);
    this._sqlService.fetchZohoSetupFeatures(project.ProjectNo).subscribe({
      next: (data) => { this.features.set(data); this.isLoadingFeatures.set(false); },
      error: () => this.isLoadingFeatures.set(false),
    });

    if (project.ZohoProjectId) {
      this.loadZohoTasks(project.ZohoProjectId);
    }
  }

  private loadZohoTasks(zohoProjectId: string) {
    this.isLoadingZohoTasks.set(true);
    this._zohoBooksService.fetchZohoProjectTasks(zohoProjectId, this.userEmail).subscribe({
      next: (data) => {
        this.zohoTasks.set(data.task ?? []);
        this.isLoadingZohoTasks.set(false);
      },
      error: () => this.isLoadingZohoTasks.set(false),
    });
  }

  saveProjectZohoId(project: ZohoSetupProject, zohoProjectId: string | null) {
    this._sqlService.updateProjectZohoId(project.ProjectNo, zohoProjectId).subscribe();
    this.projects.update((list) =>
      list.map((p) => p.ProjectNo === project.ProjectNo ? { ...p, ZohoProjectId: zohoProjectId } : p)
    );
    if (this.selectedProject()?.ProjectNo === project.ProjectNo) {
      this.selectedProject.update((p) => p ? { ...p, ZohoProjectId: zohoProjectId } : null);
      this.zohoTasks.set([]);
      if (zohoProjectId) {
        this.loadZohoTasks(zohoProjectId);
      }
    }
  }

  saveFeatureZohoId(feature: ZohoSetupFeature, zohoTaskId: string | null) {
    const projectNo = this.selectedProject()!.ProjectNo;
    this._sqlService.updateFeatureZohoId(projectNo, feature.PhaseNo, zohoTaskId).subscribe();
    this.features.update((list) =>
      list.map((f) => f.PhaseNo === feature.PhaseNo ? { ...f, ZohoTaskId: zohoTaskId } : f)
    );
  }

  getZohoProjectName(projectId: string | null): string {
    if (!projectId) return '';
    return this.zohoProjects().find((p) => p.project_id === projectId)?.project_name ?? '';
  }

  getZohoTaskName(taskId: string | null): string {
    if (!taskId) return '';
    return this.zohoTasks().find((t) => t.task_id === taskId)?.task_name ?? '';
  }

  onProjectInput(event: Event): void {
    this.zohoProjectSearch.set((event.target as HTMLInputElement).value);
  }

  onTaskInput(event: Event): void {
    this.zohoTaskSearch.set((event.target as HTMLInputElement).value);
  }

  onProjectSelected(row: ZohoSetupProject, event: MatAutocompleteSelectedEvent): void {
    this.saveProjectZohoId(row, event.option.value);
    this.zohoProjectSearch.set('');
  }

  onTaskSelected(row: ZohoSetupFeature, event: MatAutocompleteSelectedEvent): void {
    this.saveFeatureZohoId(row, event.option.value);
    this.zohoTaskSearch.set('');
  }

  onProjectTabKey(event: Event, trigger: MatAutocompleteTrigger, row: ZohoSetupProject, inputEl: HTMLInputElement): void {
    if (trigger.panelOpen && trigger.activeOption) {
      (event as KeyboardEvent).preventDefault();
      const value = trigger.activeOption.value;
      this.saveProjectZohoId(row, value);
      inputEl.value = this.getZohoProjectName(value);
      this.zohoProjectSearch.set('');
    }
  }

  onTaskTabKey(event: Event, trigger: MatAutocompleteTrigger, row: ZohoSetupFeature, inputEl: HTMLInputElement): void {
    if (trigger.panelOpen && trigger.activeOption) {
      (event as KeyboardEvent).preventDefault();
      const value = trigger.activeOption.value;
      this.saveFeatureZohoId(row, value);
      inputEl.value = this.getZohoTaskName(value);
      this.zohoTaskSearch.set('');
    }
  }
}
